import { Fragment, useMemo } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { defineMessages, IntlProvider, useIntl } from 'react-intl';
import { useEffectOnMount } from '@onramp/hooks/useEffectOnMount';
import type { InitParamErrors } from '@onramp/types/initParamErrors';
import exitWidget from '@onramp/utils/exitWidget';
import { genericMessages } from '@onramp/utils/genericMessages';
import { isMobileExperience } from '@onramp/utils/postMessage';
import { useThemeSpectrum } from '@onramp/utils/useThemeSpectrum';
import { wrapWithCallOnce } from '@onramp/utils/wrapWithCallOnce';
import { GenericErrorScreenV2 } from '@onramp/v2/client/components/errors/GenericErrorScreenV2';
import { styled } from 'linaria/react';
import { useRouter } from 'next/router';
import { snakeCase } from '@cbhq/cds-utils';
import { Pictogram } from '@cbhq/cds-web/illustrations';
import { GridColumn } from '@cbhq/cds-web/layout';
import { Box } from '@cbhq/cds-web/layout/Box';
import { Grid } from '@cbhq/cds-web/layout/Grid';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Link } from '@cbhq/cds-web/typography/Link';
import { TextBody } from '@cbhq/cds-web/typography/TextBody';
import { logMetric, MetricType } from '@cbhq/client-analytics';
import { AppThemeProvider } from './AppThemeProvider';
type InitParamsErrorViewProps = {
  errors: InitParamErrors;
} & Required<Pick<ComponentProps<typeof IntlProvider>, 'locale' | 'messages'>>;
export function InitParamsErrorView({
  errors,
  locale,
  messages: providerMessages
}: InitParamsErrorViewProps) {
  const spectrum = useThemeSpectrum();
  const DEFAULT_LOCALE = useRouter().locale;
  useEffectOnMount(() => {
    logInitParamErrorsMetric(errors);
  });
  return <AppThemeProvider spectrum={spectrum} widgetBackgroundVariant="solid">
      <IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={providerMessages}>
        <InternalInitParamsErrorView errors={errors} />
      </IntlProvider>
    </AppThemeProvider>;
}
type InternalInitParamsErrorViewProps = {
  errors: InitParamErrors;
};
function InternalInitParamsErrorView({
  errors
}: InternalInitParamsErrorViewProps) {
  const {
    formatMessage
  } = useIntl();
  const errorProps = useMemo<ComponentProps<typeof GenericErrorScreenV2>>(() => {
    const errorMessages: ReactNode[] = [];
    let title = formatMessage(messages.missingOrInvalidParamsTitle);
    if (errors.destinationWalletsDeprecated) {
      errorMessages.push(formatMessage(messages.destinationWalletsDeprecated, {
        initParametersDocsLink,
        inlineCode
      }));
    }

    // The error is only visible to developers for pre-prod testing
    if (errors?.appTierReachedMaxAttempts) {
      errorMessages.push(formatMessage(messages.appTierReachedMaxAttempts, {
        inlineCode,
        verifyProjectLink
      }));
    }
    if (errors?.invalidAppId) {
      errorMessages.push(formatMessage(messages.invalidAppId, {
        inlineCode,
        portalLink
      }));
    }
    switch (errors?.appIdAddressesAndSessionToken) {
      case 'all_missing':
        {
          errorMessages.push(formatMessage(messages.missingAppIdAddressesAndSessionToken, {
            initDocsLink,
            inlineCode
          }));
          break;
        }
      case 'secure_init_required':
        {
          errorMessages.push(formatMessage(messages.secureInitRequired, {
            inlineCode,
            secureInitDocsLink
          }));
          break;
        }
      case 'expired_session_token':
        {
          errorMessages.push(formatMessage(messages.expiredSessionToken, {
            inlineCode,
            secureInitDocsLink
          }));
          break;
        }
      case 'invalid_session_token':
        {
          errorMessages.push(formatMessage(messages.invalidParam, {
            inlineCode,
            param: 'sessionToken',
            link: secureInitDocsLink
          }));
          break;
        }
      case 'client_session_mismatch':
        {
          errorMessages.push(formatMessage(messages.clientSessionMismatch, {
            supportLink: onrampSupportMailtoLink,
            supportEmail: unauthorizedAccessMailto
          }));
          title = formatMessage(messages.unauthorizedTitle);
          break;
        }
      case 'app_id_without_addresses':
        {
          errorMessages.push(formatMessage(messages.appIdWithoutAddresses, {
            inlineCode,
            initParametersDocsLink
          }));
          break;
        }
      case 'addresses_without_app_id':
        {
          errorMessages.push(formatMessage(messages.addressesWithoutAppId, {
            inlineCode,
            initParametersDocsLink
          }));
          break;
        }
      case 'destination_wallets_without_app_id':
        {
          errorMessages.push(formatMessage(messages.destinationWalletsWithoutAppId, {
            inlineCode,
            initParametersDocsLink
          }));
          break;
        }
      default:
        {
          break;
        }
    }
    for (const param of errors?.missingParams ?? []) {
      errorMessages.push(formatMessage(messages.missingParam, {
        inlineCode,
        param,
        link: initParametersDocsLink
      }));
    }
    for (const param of errors?.invalidParams ?? []) {
      errorMessages.push(formatMessage(messages.invalidParam, {
        inlineCode,
        param,
        link: initParametersDocsLink
      }));
    }
    const description = <VStack spacingTop={1} gap={2} width="100%">
        <ErrorListStyles>
          <TextBody as="p" color="foregroundMuted">
            {formatMessage(messages.pleaseFixTheFollowing)}
          </TextBody>
          <Grid className="error-list" templateColumns="min-content 1fr">
            {errorMessages.map((error, index) => <Fragment
          // eslint-disable-next-line react/no-array-index-key
          key={index}>
                <GridColumn>
                  <TextBody as="p" color="foregroundMuted">
                    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                    {index + 1}.
                  </TextBody>
                </GridColumn>
                <GridColumn>
                  <TextBody as="p" color="foregroundMuted">
                    {error}
                  </TextBody>
                </GridColumn>
              </Fragment>)}
          </Grid>
        </ErrorListStyles>
      </VStack>;
    return {
      title,
      description
    };
  }, [errors.destinationWalletsDeprecated, errors?.invalidAppId, errors?.appIdAddressesAndSessionToken, errors?.missingParams, errors?.invalidParams, errors?.appTierReachedMaxAttempts, formatMessage]);
  if (errors.dappWalletApp) {
    return <GenericErrorScreenV2 title={formatMessage(messages.dappWalletAppTitle)} pageTitle={formatMessage(genericMessages.coinbaseOnramp)} description={
    // Need to pass whole component because the link prevents
    // the error screen from wrapping it with TextBody
    <TextBody as="p" color="foregroundMuted" spacingBottom={3} align="center">
            {formatMessage(messages.dappWalletAppDescription, {
        dappWalletHelpLink
      })}
          </TextBody>} onBack={isMobileExperience() ? exitWidget : undefined} />;
  }
  if (errors.oldBaseAppVersion) {
    return <GenericErrorScreenV2 title={formatMessage(messages.oldBaseAppVersionTitle)} pageTitle={formatMessage(genericMessages.coinbaseOnramp)} description={formatMessage(messages.oldBaseAppVersionDescription)} onBack={isMobileExperience() ? exitWidget : undefined} />;
  }
  if (errors?.appIdAddressesAndSessionToken === 'secure_init_required_for_cb_wallet') {
    return <GenericErrorScreenV2 title={formatMessage(messages.cbWalletSecureInitRequiredTitle)} pageTitle={formatMessage(genericMessages.coinbaseOnramp)} description={formatMessage(messages.cbWalletSecureInitRequiredDescription)} onBack={isMobileExperience() ? exitWidget : undefined} />;
  }
  return <GenericErrorScreenV2 alignTitleStart justify="flex-start" title={errorProps.title} pageTitle={formatMessage(genericMessages.coinbaseOnramp)} icon={<Box justifyContent="flex-start" width="100%" spacingTop={2} spacingBottom={2}>
          <Pictogram name="warning" />
        </Box>} description={errorProps.description} />;
}
const ErrorListStyles = /*#__PURE__*/styled("div")({
  name: "ErrorListStyles",
  class: "e18hmtyw"
});
function makeI18nLink(href: string) {
  return function link(text: NonNullable<ReactNode>) {
    return <Link href={href} target="_blank">
        {text}
      </Link>;
  };
}
const portalLink = makeI18nLink('https://portal.cdp.coinbase.com');
const initDocsLink = makeI18nLink('https://docs.cdp.coinbase.com/onramp/docs/api-onramp-initializing');
const initParametersDocsLink = makeI18nLink('https://docs.cdp.coinbase.com/onramp/docs/api-onramp-initializing#onramp-url-parameters');
const secureInitDocsLink = makeI18nLink('https://docs.cdp.coinbase.com/onramp/docs/api-onramp-initializing#getting-a-session-token');
const verifyProjectLink = makeI18nLink('https://support.cdp.coinbase.com/onramp-onboarding');
/*
  We are deliberately vague for IP validation errors, and either users or developers or attackers 
  may see the error message - currently sending all parties to user support.
  For context, see https://github.cbhq.net/payments/onramp-widget/pull/3194#discussion_r2799944
*/
const unauthorizedAccessMailto = 'onrampsupport@coinbase.com';
const onrampSupportMailtoLink = makeI18nLink(`mailto:${unauthorizedAccessMailto}?subject=Onramp unauthorized error&body=I received an Unauthorized Access error when trying to exchange crypto`);
const dappWalletHelpLink = makeI18nLink('https://help.coinbase.com/en/coinbase/trading-and-funding/trade-on-dex/web3-wallet-sunset');
function inlineCode(text: ReactNode) {
  return <Box display="inline-block" background="secondary" borderColor="line" borderRadius="roundedSmall">
      <InlineCodeStyles>
        <TextBody as="span" color="foreground" mono>
          {text}
        </TextBody>
      </InlineCodeStyles>
    </Box>;
}
const InlineCodeStyles = /*#__PURE__*/styled("div")({
  name: "InlineCodeStyles",
  class: "ikd6qou"
});
export const messages = defineMessages({
  addressesWithoutAppId: {
    id: 'InitParamsErrorView.addressesWithoutAppId',
    defaultMessage: 'When providing <inlineCode>addresses</inlineCode>, <inlineCode>appId</inlineCode> (also known as Project ID) is also required. <initParametersDocsLink>View docs</initParametersDocsLink>',
    description: 'Error when one parameter is passed without another parameter it expects. Do not translate the contents of inlineCode'
  },
  appIdWithoutAddresses: {
    id: 'InitParamsErrorView.appIdWithoutAddresses',
    defaultMessage: 'When providing <inlineCode>appId</inlineCode> (also known as Project ID), <inlineCode>addresses</inlineCode> is also required. <initParametersDocsLink>View docs</initParametersDocsLink>',
    description: 'Error when one parameter is passed without another parameter it expects. Do not translate the contents of inlineCode'
  },
  destinationWalletsDeprecated: {
    id: 'InitParamsErrorView.destinationWalletsDeprecated',
    defaultMessage: '<inlineCode>destinationWallets</inlineCode> is deprecated and has been replaced by <inlineCode>addresses</inlineCode> and <inlineCode>assets</inlineCode>. <initParametersDocsLink>View docs</initParametersDocsLink>. Note: This warning is only shown when you have another parameter error.',
    description: 'Warning when a deprecated parameter is provided & an error occurs. Do not translate the contents of inlineCode'
  },
  destinationWalletsWithoutAppId: {
    id: 'InitParamsErrorView.destinationWalletsWithoutAppId',
    defaultMessage: 'When providing <inlineCode>destinationWallets</inlineCode>, <inlineCode>appId</inlineCode> (also known as Project ID) is also required. <initParametersDocsLink>View docs</initParametersDocsLink>',
    description: 'Error when one parameter is passed without another parameter it expects. Do not translate the contents of inlineCode'
  },
  expiredSessionToken: {
    id: 'InitParamsErrorView.expiredSessionToken',
    defaultMessage: 'Each <inlineCode>sessionToken</inlineCode> can only be used once and this one has already been used to launch Coinbase Onramp. <secureInitDocsLink>View docs</secureInitDocsLink>',
    description: 'Error when a one-time-use sessionToken is reused'
  },
  invalidParam: {
    id: 'InitParamsErrorView.invalidParam',
    defaultMessage: 'Invalid <inlineCode>{param}</inlineCode>. <link>View docs</link>',
    description: 'Generic error when an initialization parameter is provided with a bad value. Do not translate the contents of inlineCode'
  },
  invalidAppId: {
    id: 'InitParamsErrorView.invalidAppId',
    defaultMessage: 'Invalid <inlineCode>appId</inlineCode>. Your <inlineCode>appId</inlineCode> (also referred to as Project ID) can be found on the <portalLink>Coinbase Developer Platform</portalLink> dashboard.',
    description: 'Error when the appId initialization parameter is invalid or missing. Do not translate the contents of inlineCode.'
  },
  clientSessionMismatch: {
    id: 'InitParamsErrorView.clientSessionMismatch',
    defaultMessage: `This request is not authorized. Please contact <supportLink>{supportEmail}</supportLink>.`,
    description: 'Deliberately low-information error - we just say unauthorized without revealing the cause, hinting at a location problem (support can help figure out what caused it).'
  },
  missingAppIdAddressesAndSessionToken: {
    id: 'InitParamsErrorView.missingAppIdAddressesAndSessionToken',
    defaultMessage: 'We require either: (1) <inlineCode>appId</inlineCode> (also referred to as Project ID) and <inlineCode>addresses</inlineCode>, or (2) <inlineCode>sessionToken</inlineCode>. <initDocsLink>View Docs</initDocsLink>',
    description: 'Error when all parameters of both options of parameters sets are missing. Do not translate the contents of inlineCode.'
  },
  missingOrInvalidParamsTitle: {
    id: 'InitParamsErrorView.missingOrInvalidParamsTitle',
    defaultMessage: 'Missing or invalid parameters',
    description: 'Title for view of missing or invalid parameters'
  },
  unauthorizedTitle: {
    id: `InitParamsErrorGuard.unauthorizedTitle`,
    defaultMessage: 'Unauthorized Access',
    description: 'Title for view of unauthorized access'
  },
  missingParam: {
    id: 'InitParamsErrorView.missingParam',
    defaultMessage: 'Missing <inlineCode>{param}</inlineCode>. <link>View docs</link>',
    description: 'Generic error for individual missing initialization parameter'
  },
  pleaseFixTheFollowing: {
    id: 'InitParamsErrorView.pleaseFixTheFollowing',
    defaultMessage: 'Please fix the following errors:',
    description: 'Header for view of errors'
  },
  secureInitRequired: {
    id: 'InitParamsErrorView.secureInitRequired',
    defaultMessage: 'Your project is configured to require secure initialization and requires a <inlineCode>sessionToken</inlineCode>. <secureInitDocsLink>View docs</secureInitDocsLink>',
    description: 'Error when the app is configured to require secure initialization and the user is attempting to initialize without it'
  },
  cbWalletSecureInitRequiredTitle: {
    id: 'InitParamsErrorView.cbWalletSecureInitRequiredTitle',
    defaultMessage: 'App update required',
    description: 'Title for CB Wallet users when the app is configured to require secure initialization and the user is attempting to initialize without it'
  },
  cbWalletSecureInitRequiredDescription: {
    id: 'InitParamsErrorView.cbWalletSecureInitRequiredDescription',
    defaultMessage: 'Please update your Base app to continue.',
    description: 'Custom error message for CB Wallet users when the app is configured to require secure initialization and the user is attempting to initialize without it'
  },
  appTierReachedMaxAttempts: {
    id: 'InitParamsErrorView.appTierReachedMaxAttempts',
    defaultMessage: 'This app has reached the maximum number of attempts for the pre-prod app tier. If you are the app owner, apply for full access <verifyProjectLink>here</verifyProjectLink>.',
    description: 'Error message when an app has reached the maximum number of attempts allowed for its pre-prod app tier, with a call-to-action for app owners to apply for full access'
  },
  dappWalletAppTitle: {
    id: 'InitParamsErrorView.dappWalletAppTitle',
    defaultMessage: 'This feature is no longer supported',
    description: 'Title for Dapp Wallet users informing them that support is dropped'
  },
  dappWalletAppDescription: {
    id: 'InitParamsErrorView.dappWalletAppDescription',
    defaultMessage: 'You are on an unsupported version of Wallet (now Base) & this feature has been removed. More information is available <dappWalletHelpLink>here</dappWalletHelpLink>.',
    description: 'Error message for Dapp Wallet users informing them that support is dropped'
  },
  oldBaseAppVersionTitle: {
    id: 'InitParamsErrorView.oldBaseAppVersionTitle',
    defaultMessage: 'App update required',
    description: 'Title for users on an old Base app version that is no longer supported'
  },
  oldBaseAppVersionDescription: {
    id: 'InitParamsErrorView.oldBaseAppVersionDescription',
    defaultMessage: 'Please update your Base app to buy crypto.',
    description: 'Error message for users on an old Base app version that is no longer supported'
  }
});
const logInitParamErrorsMetric = wrapWithCallOnce(function logInitParamErrorsMetric(errors: InitParamErrors) {
  // Allowed tag characters: https://docs.datadoghq.com/getting_started/tagging/#define-tags
  const formattedTags: Record<string, string | boolean> = {};
  const tagEntries = Object.entries(errors).map(([key, value]) => ([snakeCase(key), Array.isArray(value) ? snakeCase(value.join('.')) : value] as const));
  for (const [key, value] of tagEntries) {
    formattedTags[key] = value;
  }
  logMetric({
    metricName: 'cbpay.init_param_errors',
    metricType: MetricType.count,
    value: 1,
    tags: {
      ...formattedTags
    }
  });
}, {
  argsToKeyFn() {
    return 'init-param-errors';
  }
});

require("./InitParamsErrorView.linaria.module.css!=!../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./InitParamsErrorView.tsx");